import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Car, Calculator, InfoIcon } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './AutoLoanCalculatorPage/components/CalculationForm';
import ResultsOverview from './AutoLoanCalculatorPage/components/ResultsOverview';
import PaymentSchedule from './AutoLoanCalculatorPage/components/PaymentSchedule';
import QuickAdjustmentSidebar from './AutoLoanCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './AutoLoanCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './AutoLoanCalculatorPage/components/TipsSidebar';
import InfoContent from './AutoLoanCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  carPrice: '1500000',
  downPayment: '300000',
  downPaymentPercent: '20',
  loanTerm: '5', // Years
  interestRate: '9.5', // Annual percentage
  paymentType: 'annuity', // 'annuity' or 'differentiated'
  startDate: new Date().toISOString().split('T')[0],
  carType: 'new', // 'new' or 'used'
  carAge: '0', // Возраст автомобиля (для б/у)
  includeInsurance: true,
  insuranceType: 'full', // 'full' (КАСКО+ОСАГО) или 'basic' (только ОСАГО)
  insuranceCost: '60000', // Стоимость страховки за год
  includeAdditionalFees: false,
  registrationFee: '2000', // Регистрационный сбор
  additionalServices: '0', // Дополнительные услуги
  includeEarlyRepayment: false,
  earlyRepaymentType: 'reduction', // 'reduction' (срок) или 'payment' (платеж)
  earlyRepaymentAmount: '100000',
  earlyRepaymentPeriod: 'quarterly', // 'monthly', 'quarterly', 'semi_annually', 'annually', 'once'
  earlyRepaymentStartMonth: '12', // Месяц начала досрочного погашения
  includeTradeIn: false,
  tradeInValue: '400000', // Стоимость автомобиля для Trade-In
};

// Определение типов автомобилей (новые/б/у)
export const carTypes = [
  { id: 'new', name: 'Новый автомобиль', defaultRate: 9.5 },
  { id: 'used', name: 'Подержанный автомобиль', defaultRate: 12.5 }
];

// Определение типов страхования
export const insuranceTypes = [
  { id: 'full', name: 'Полное КАСКО + ОСАГО' },
  { id: 'basic', name: 'Только ОСАГО' }
];

// Определение периодов досрочного погашения
export const earlyRepaymentPeriods = [
  { id: 'monthly', name: 'Ежемесячно' },
  { id: 'quarterly', name: 'Ежеквартально' },
  { id: 'semi_annually', name: 'Раз в полгода' },
  { id: 'annually', name: 'Ежегодно' },
  { id: 'once', name: 'Разово' }
];

/**
 * AutoLoanCalculatorPage Component - Полный калькулятор автокредита с различными параметрами.
 */
const AutoLoanCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => {
    // Используем getToolById для получения информации о калькуляторе
    // В данном случае предполагаем, что ID инструмента - 'auto-loan-calculator'
    // Если такого инструмента нет в данных, используем объект с дефолтными значениями
    const toolData = getToolById('auto-loan-calculator');
    if (toolData) return toolData;
    
    return {
      id: 'auto-loan-calculator',
      name: 'Калькулятор автокредита',
      metaTitle: 'Калькулятор автокредита | Gensio',
      metaDescription: 'Полный расчет автокредита: ежемесячный платеж, процентная ставка, переплата, график платежей с учетом первоначального взноса, страховки и дополнительных услуг.',
      category: 'finance',
      relatedTools: ['loan-calculator', 'mortgage-calculator', 'currency-converter'],
      iconName: 'car'
    };
  }, []);
  
  const pageTitle = tool?.metaTitle || 'Калькулятор автокредита | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный расчет автокредита: ежемесячный платеж, процентная ставка, переплата, график платежей с учетом первоначального взноса, страховки и дополнительных услуг.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedLoans, setSavedLoans] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных автокредитов из localStorage ---
  useEffect(() => {
    const savedLoansData = localStorage.getItem('savedAutoLoans');
    if (savedLoansData) {
      try {
        setSavedLoans(JSON.parse(savedLoansData));
      } catch (e) {
        console.error("Error loading saved auto loans:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const findRate = (id) => {
        const found = carTypes.find(t => t.id === id);
        return found ? found.defaultRate.toString() : initialFormData.interestRate;
      };

      switch (subToolId) {
        case 'new-car':
          newState.carType = 'new';
          newState.interestRate = findRate('new');
          break;
        case 'used-car':
          newState.carType = 'used';
          newState.interestRate = findRate('used');
          newState.carAge = '3';
          break;
        case 'no-down-payment':
          newState.downPayment = '0';
          newState.downPaymentPercent = '0';
          newState.interestRate = '14.5'; // Обычно кредиты без первоначального взноса имеют более высокую ставку
          break;
        case 'trade-in':
          newState.includeTradeIn = true;
          break;
        case 'with-insurance':
          newState.includeInsurance = true;
          newState.insuranceType = 'full';
          break;
        case 'with-early-repayment':
          newState.includeEarlyRepayment = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Сохранение автокредита в localStorage
  const saveAutoLoan = (name) => {
    if (!results) return;

    const newSavedLoan = {
      id: Date.now().toString(),
      name: name || `Автокредит на ${formData.carPrice}₽ на ${formData.loanTerm} лет`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      carType: formData.carType,
      paymentType: formData.paymentType
    };

    const updatedSavedLoans = [...savedLoans, newSavedLoan];
    setSavedLoans(updatedSavedLoans);
    localStorage.setItem('savedAutoLoans', JSON.stringify(updatedSavedLoans));

    return newSavedLoan;
  };

  // Удаление сохраненного автокредита
  const deleteSavedLoan = (id) => {
    const updatedSavedLoans = savedLoans.filter(loan => loan.id !== id);
    setSavedLoans(updatedSavedLoans);
    localStorage.setItem('savedAutoLoans', JSON.stringify(updatedSavedLoans));
  };

  // Загрузка сохраненного автокредита
  const loadSavedLoan = (id) => {
    const loanToLoad = savedLoans.find(loan => loan.id === id);
    if (loanToLoad) {
      setFormData(loanToLoad.formData);
      setResults(loanToLoad.results);
      // Если график платежей нужно загрузить, можно сохранять его вместе с результатами
      // или перерассчитывать
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Car className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор автокредита'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              schedule={schedule}
              setSchedule={setSchedule}
              carTypes={carTypes}
              insuranceTypes={insuranceTypes}
              earlyRepaymentPeriods={earlyRepaymentPeriods}
              saveAutoLoan={saveAutoLoan}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <PaymentSchedule 
                  schedule={schedule} 
                  results={results}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент об автокредитах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения кредитов --- */}
            <ComparisonSidebar 
              savedLoans={savedLoans}
              deleteSavedLoan={deleteSavedLoan}
              loadSavedLoan={loadSavedLoan}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Payment Schedule Table */
        .payment-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .payment-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default AutoLoanCalculatorPage;